import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  input,
  OnDestroy,
  signal,
  viewChild,
} from '@angular/core';
import { TranslocoModule } from '@jsverse/transloco';

export interface ChartConfig {
  type: string;
  title?: string;
  description?: string;
  xKey?: string;
  yKeys?: string[];
  xAxisKey?: string;
  yAxisKey?: string;
  categoryKey?: string;
  legend?: boolean;
  takeaway?: string;
  colors?: Record<string, string>;
}

export interface ChartData {
  config: ChartConfig;
  data: Record<string, unknown>[];
}

interface LegendItem {
  label: string;
  color: string;
}

interface BarDatum {
  label: string;
  labelX: number;
  x: number;
  y: number;
  width: number;
  height: number;
  color: string;
}

interface LinePoint {
  x: number;
  y: number;
}

interface LineSeries {
  key: string;
  path: string;
  points: LinePoint[];
  color: string;
}

interface XLabel {
  text: string;
  x: number;
}

interface PieSlice {
  label: string;
  path: string;
  color: string;
}

const MAX_BAR_WIDTH = 80;

const CHART_COLORS = [
  '#3b82f6', // blue
  '#10b981', // green
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#84cc16', // lime
];

@Component({
  selector: 'app-ai-chart',
  imports: [CommonModule, TranslocoModule],
  templateUrl: './ai-chart.component.html',
  styleUrls: ['./ai-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiChartComponent implements AfterViewInit, OnDestroy {
  readonly chartData = input.required<ChartData>();

  private readonly chartContainerRef = viewChild<ElementRef<HTMLDivElement>>('chartContainer');

  private resizeObserver: ResizeObserver | null = null;

  private readonly containerWidth = signal<number>(320);

  readonly width = computed<number>(() => Math.max(320, this.containerWidth()));

  readonly height = computed<number>(() => {
    const width = this.width();
    const chartType = this.chartType();
    const ratio = chartType === 'pie' ? 0.55 : 0.45;
    return Math.max(220, Math.min(360, Math.round(width * ratio)));
  });

  readonly margin = { top: 20, right: 20, bottom: 48, left: 48 };

  readonly innerWidth = computed<number>(() => this.width() - this.margin.left - this.margin.right);
  readonly innerHeight = computed<number>(
    () => this.height() - this.margin.top - this.margin.bottom
  );

  readonly chartType = computed<string>(() => {
    const config: ChartConfig = this.chartData().config;
    return config.type || 'bar';
  });

  readonly showLegend = computed<boolean>(() => {
    const config: ChartConfig = this.chartData().config;
    const yKeys: string[] = config.yKeys ?? [];
    return config.legend !== false && yKeys.length > 1;
  });

  readonly legendItems = computed<LegendItem[]>(() => {
    const config: ChartConfig = this.chartData().config;
    const yKeys: string[] = config.yKeys ?? [];
    return yKeys.map(
      (key: string, index: number): LegendItem => ({
        label: key,
        color: config.colors?.[key] ?? CHART_COLORS[index % CHART_COLORS.length],
      })
    );
  });

  readonly barData = computed<BarDatum[]>(() => {
    const chartData: ChartData = this.chartData();
    const config: ChartConfig = chartData.config;
    const data: Record<string, unknown>[] = chartData.data;
    if (data.length === 0) {
      return [];
    }

    const maxBars: number = 10;
    const visibleData: Record<string, unknown>[] = data.slice(0, maxBars);
    const fallbackKeys: string[] = Object.keys(data[0] ?? {});
    const xKey: string = config.xKey ?? config.xAxisKey ?? fallbackKeys[0];
    const yKeys: string[] = config.yKeys ?? [config.yAxisKey ?? fallbackKeys[1]];

    const allValues: number[] = yKeys.flatMap((yKey: string) =>
      visibleData.map((datum: Record<string, unknown>) => Number(datum[yKey]) || 0)
    );
    const maxValue: number = Math.max(...allValues, 1);
    const groupGap: number = 12;
    const naturalGroupWidth: number = this.innerWidth() / visibleData.length - groupGap;
    const naturalBarWidth: number = naturalGroupWidth / yKeys.length;
    const barWidth: number = Math.min(MAX_BAR_WIDTH, Math.max(20, naturalBarWidth));
    const groupWidth: number = barWidth * yKeys.length;
    const totalContentWidth: number =
      visibleData.length * groupWidth + (visibleData.length - 1) * groupGap;
    const offsetX: number = Math.max(0, (this.innerWidth() - totalContentWidth) / 2);

    return visibleData.flatMap((datum: Record<string, unknown>, groupIndex: number): BarDatum[] => {
      const groupX: number = offsetX + groupIndex * (groupWidth + groupGap);
      return yKeys.map((yKey: string, keyIndex: number): BarDatum => {
        const value: number = Number(datum[yKey]) || 0;
        const barHeight: number = (value / maxValue) * this.innerHeight();
        const innerGap: number = yKeys.length > 1 ? 2 : 0;
        return {
          label: keyIndex === 0 ? String(datum[xKey] ?? '') : '',
          labelX: groupX + groupWidth / 2,
          x: groupX + keyIndex * barWidth,
          y: this.innerHeight() - barHeight,
          width: barWidth - innerGap,
          height: barHeight,
          color: config.colors?.[yKey] ?? CHART_COLORS[keyIndex % CHART_COLORS.length],
        };
      });
    });
  });

  readonly lineData = computed<LineSeries[]>(() => {
    const chartData: ChartData = this.chartData();
    const config: ChartConfig = chartData.config;
    const data: Record<string, unknown>[] = chartData.data;
    if (data.length === 0) {
      return [];
    }

    const fallbackKeys: string[] = Object.keys(data[0] ?? {});
    const yKeys: string[] = config.yKeys ?? [config.yAxisKey ?? fallbackKeys[1]];

    const allValues: number[] = yKeys.flatMap((yKey: string) =>
      data.map((datum: Record<string, unknown>) => Number(datum[yKey]) || 0)
    );
    const maxValue: number = Math.max(...allValues, 1);
    const xStep: number = this.innerWidth() / Math.max(data.length - 1, 1);

    return yKeys.map((yKey: string, keyIndex: number): LineSeries => {
      const points: LinePoint[] = data.map((datum: Record<string, unknown>, index: number) => {
        const value: number = Number(datum[yKey]) || 0;
        return {
          x: index * xStep,
          y: this.innerHeight() - (value / maxValue) * this.innerHeight(),
        };
      });

      const path: string = points
        .map(
          (point: LinePoint, index: number) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`
        )
        .join(' ');

      return {
        key: yKey,
        path,
        points,
        color: config.colors?.[yKey] ?? CHART_COLORS[keyIndex % CHART_COLORS.length],
      };
    });
  });

  readonly xLabels = computed<XLabel[]>(() => {
    const chartData: ChartData = this.chartData();
    const config: ChartConfig = chartData.config;
    const data: Record<string, unknown>[] = chartData.data;
    if (data.length === 0) {
      return [];
    }

    const fallbackKeys: string[] = Object.keys(data[0] ?? {});
    const xKey: string = config.xKey ?? config.xAxisKey ?? fallbackKeys[0];
    const xStep: number = this.innerWidth() / Math.max(data.length - 1, 1);

    // Show max 6 labels
    const step: number = Math.ceil(data.length / 6);
    return data
      .filter((_, index: number) => index % step === 0)
      .map(
        (datum: Record<string, unknown>, index: number): XLabel => ({
          text: String(datum[xKey] ?? ''),
          x: index * step * xStep,
        })
      );
  });

  readonly pieData = computed<PieSlice[]>(() => {
    const chartData: ChartData = this.chartData();
    const config: ChartConfig = chartData.config;
    const data: Record<string, unknown>[] = chartData.data;
    if (data.length === 0) {
      return [];
    }

    const fallbackKeys: string[] = Object.keys(data[0] ?? {});
    const xKey: string = config.xKey ?? config.xAxisKey ?? fallbackKeys[0];
    const yKeys: string[] = config.yKeys ?? [config.yAxisKey ?? fallbackKeys[1]];
    const yKey: string = yKeys[0];

    const values: number[] = data.slice(0, 8).map((datum: Record<string, unknown>) => {
      return Number(datum[yKey]) || 0;
    });
    const total: number = values.reduce((acc: number, value: number) => acc + value, 0) || 1;
    const radius: number = Math.min(this.innerWidth(), this.innerHeight()) / 2;

    let startAngle: number = 0;
    return data.slice(0, 8).map((datum: Record<string, unknown>, index: number): PieSlice => {
      const value: number = Number(datum[yKey]) || 0;
      const angle: number = (value / total) * 2 * Math.PI;
      const endAngle: number = startAngle + angle;

      const x1: number = radius * Math.cos(startAngle - Math.PI / 2);
      const y1: number = radius * Math.sin(startAngle - Math.PI / 2);
      const x2: number = radius * Math.cos(endAngle - Math.PI / 2);
      const y2: number = radius * Math.sin(endAngle - Math.PI / 2);
      const largeArc: number = angle > Math.PI ? 1 : 0;

      const path: string = `M 0 0 L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`;
      startAngle = endAngle;

      return {
        label: String(datum[xKey] ?? ''),
        path,
        color: CHART_COLORS[index % CHART_COLORS.length],
      };
    });
  });

  ngAfterViewInit(): void {
    this.updateContainerWidth();
    const chartContainer: ElementRef<HTMLDivElement> | undefined = this.chartContainerRef();
    if (!chartContainer) {
      return;
    }
    this.resizeObserver = new ResizeObserver(() => this.updateContainerWidth());
    this.resizeObserver.observe(chartContainer.nativeElement);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
  }

  private updateContainerWidth(): void {
    const chartContainer: ElementRef<HTMLDivElement> | undefined = this.chartContainerRef();
    if (!chartContainer) {
      return;
    }
    const nextWidth: number = chartContainer.nativeElement.clientWidth;
    if (Number.isFinite(nextWidth) && nextWidth > 0) {
      this.containerWidth.set(Math.floor(nextWidth));
    }
  }
}
